import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks';
import { selectUser } from '../store/slices/authSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import apiClient from '../services/api';
import {
  AIProvider,
  AIConfiguration,
  AIConfigurationUpdate,
  PromptTemplate,
  PromptTemplateCreate,
  PromptTemplateUpdate,
  AIUsageAnalytics,
  FeatureFlag,
  FeatureFlagUpdate,
  AIUsageLog,
  PaginatedAIUsageLogsResponse,
  AIImageProvider,
  AIImageConfiguration,
  AIImageConfigurationUpdate,
  ImagePromptTemplate,
  ImagePromptTemplateCreate,
  ImagePromptTemplateUpdate,
  PaginatedAIImageUsageLogsResponse
} from '../types/api';
import './AdminAIDashboard.css';

type TabType = 'overview' | 'configuration' | 'prompts' | 'analytics' | 'feature-flags' | 'usage-logs' | 'image-generation';

const AdminAIDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for different sections
  const [aiProviders, setAiProviders] = useState<AIProvider[]>([]);
  const [aiConfigurations, setAiConfigurations] = useState<AIConfiguration[]>([]);
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>([]);
  const [usageAnalytics, setUsageAnalytics] = useState<AIUsageAnalytics | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [usageLogs, setUsageLogs] = useState<PaginatedAIUsageLogsResponse | null>(null);

  // Image generation state
  const [imageProviders, setImageProviders] = useState<AIImageProvider[]>([]);
  const [imageConfigurations, setImageConfigurations] = useState<AIImageConfiguration[]>([]);
  const [imagePromptTemplates, setImagePromptTemplates] = useState<ImagePromptTemplate[]>([]);
  const [imageUsageLogs, setImageUsageLogs] = useState<PaginatedAIImageUsageLogsResponse | null>(null);

  // Form states
  const [editingConfig, setEditingConfig] = useState<AIConfiguration | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<PromptTemplate | null>(null);
  const [showCreatePrompt, setShowCreatePrompt] = useState(false);

  // Image form states
  const [editingImageConfig, setEditingImageConfig] = useState<AIImageConfiguration | null>(null);
  const [editingImagePrompt, setEditingImagePrompt] = useState<ImagePromptTemplate | null>(null);
  const [showCreateImagePrompt, setShowCreateImagePrompt] = useState(false);

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const isAdmin = await apiClient.verifyAdminAccess();
        if (!isAdmin) {
          navigate('/dashboard');
          return;
        }
        await loadInitialData();
      } catch (error) {
        console.error('Error checking admin access:', error);
        navigate('/dashboard');
      }
    };

    checkAdminAccess();
  }, [navigate]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load all data in parallel (including image generation data)
      const [providers, configs, templates, analytics, flags, imageProvs, imageConfigs, imageTemplates] = await Promise.all([
        apiClient.getAIProviders(),
        apiClient.getAIConfigurations(),
        apiClient.getPromptTemplates(),
        apiClient.getAIUsageAnalytics(30),
        apiClient.getFeatureFlags(),
        apiClient.getImageProviders(),
        apiClient.getImageConfigurations(),
        apiClient.getImagePromptTemplates()
      ]);

      setAiProviders(providers);
      setAiConfigurations(configs);
      setPromptTemplates(templates);
      setUsageAnalytics(analytics);
      setFeatureFlags(flags);
      setImageProviders(imageProvs);
      setImageConfigurations(imageConfigs);
      setImagePromptTemplates(imageTemplates);

    } catch (error) {
      console.error('Error loading AI data:', error);
      setError('Failed to load AI dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsageLogs = async () => {
    try {
      const logs = await apiClient.getAIUsageLogs({ page: 1, size: 50 });
      setUsageLogs(logs);
    } catch (error) {
      console.error('Error loading usage logs:', error);
      setError('Failed to load usage logs');
    }
  };

  const handleUpdateConfiguration = async (providerName: string, data: AIConfigurationUpdate) => {
    try {
      const updatedConfig = await apiClient.updateAIConfiguration(providerName, data);
      setAiConfigurations(prev =>
        prev.map(config =>
          config.provider_name === providerName ? updatedConfig : config
        )
      );
      setEditingConfig(null);
      alert('Configuration updated successfully!');
    } catch (error) {
      console.error('Error updating configuration:', error);
      alert('Failed to update configuration');
    }
  };

  const handleCreatePrompt = async (data: PromptTemplateCreate) => {
    try {
      const newTemplate = await apiClient.createPromptTemplate(data);
      setPromptTemplates(prev => [...prev, newTemplate]);
      setShowCreatePrompt(false);
      alert('Prompt template created successfully!');
    } catch (error) {
      console.error('Error creating prompt template:', error);
      alert('Failed to create prompt template');
    }
  };

  const handleUpdatePrompt = async (templateId: string, data: PromptTemplateUpdate) => {
    try {
      const updatedTemplate = await apiClient.updatePromptTemplate(templateId, data);
      setPromptTemplates(prev =>
        prev.map(template =>
          template.template_id === templateId ? updatedTemplate : template
        )
      );
      setEditingPrompt(null);
      alert('Prompt template updated successfully!');
    } catch (error) {
      console.error('Error updating prompt template:', error);
      alert('Failed to update prompt template');
    }
  };

  const handleDeletePrompt = async (templateId: string) => {
    try {
      await apiClient.deletePromptTemplate(templateId);
      setPromptTemplates(prev => prev.filter(template => template.template_id !== templateId));
      alert('Prompt template deleted successfully!');
    } catch (error) {
      console.error('Error deleting prompt template:', error);
      alert('Failed to delete prompt template');
    }
  };

  const handleUpdateFeatureFlag = async (flagName: string, data: FeatureFlagUpdate) => {
    try {
      const updatedFlag = await apiClient.updateFeatureFlag(flagName, data);
      setFeatureFlags(prev =>
        prev.map(flag =>
          flag.name === flagName ? updatedFlag : flag
        )
      );
      alert('Feature flag updated successfully!');
    } catch (error) {
      console.error('Error updating feature flag:', error);
      alert('Failed to update feature flag');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="admin-ai-dashboard">
      <div className="admin-ai-header">
        <h1>AI Administration Dashboard</h1>
        <p>Manage AI configurations, prompts, analytics, and usage</p>
      </div>

      {error && (
        <div className="admin-ai-error">
          <p>{error}</p>
          <button onClick={loadInitialData}>Retry</button>
        </div>
      )}

      <div className="admin-ai-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'configuration' ? 'active' : ''}
          onClick={() => setActiveTab('configuration')}
        >
          Configuration
        </button>
        <button
          className={activeTab === 'prompts' ? 'active' : ''}
          onClick={() => setActiveTab('prompts')}
        >
          Prompt Templates
        </button>
        <button
          className={activeTab === 'analytics' ? 'active' : ''}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
        <button
          className={activeTab === 'feature-flags' ? 'active' : ''}
          onClick={() => setActiveTab('feature-flags')}
        >
          Feature Flags
        </button>
        <button
          className={activeTab === 'usage-logs' ? 'active' : ''}
          onClick={() => {
            setActiveTab('usage-logs');
            if (!usageLogs) loadUsageLogs();
          }}
        >
          Usage Logs
        </button>
        <button
          className={activeTab === 'image-generation' ? 'active' : ''}
          onClick={() => setActiveTab('image-generation')}
        >
          Image Generation
        </button>
      </div>

      <div className="admin-ai-content">
        {activeTab === 'overview' && (
          <OverviewTab
            analytics={usageAnalytics}
            configurations={aiConfigurations}
            templates={promptTemplates}
            flags={featureFlags}
          />
        )}

        {activeTab === 'configuration' && (
          <ConfigurationTab
            providers={aiProviders}
            configurations={aiConfigurations}
            editingConfig={editingConfig}
            onEdit={setEditingConfig}
            onUpdate={handleUpdateConfiguration}
            onCancel={() => setEditingConfig(null)}
          />
        )}

        {activeTab === 'prompts' && (
          <PromptsTab
            templates={promptTemplates}
            editingPrompt={editingPrompt}
            showCreatePrompt={showCreatePrompt}
            onEdit={setEditingPrompt}
            onCreate={() => setShowCreatePrompt(true)}
            onUpdate={handleUpdatePrompt}
            onDelete={handleDeletePrompt}
            onCreateSubmit={handleCreatePrompt}
            onCancel={() => {
              setEditingPrompt(null);
              setShowCreatePrompt(false);
            }}
            configurations={aiConfigurations}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab analytics={usageAnalytics} />
        )}

        {activeTab === 'feature-flags' && (
          <FeatureFlagsTab
            flags={featureFlags}
            onUpdate={handleUpdateFeatureFlag}
          />
        )}

        {activeTab === 'usage-logs' && (
          <UsageLogsTab logs={usageLogs} />
        )}

        {activeTab === 'image-generation' && (
          <ImageGenerationTab
            providers={imageProviders}
            configurations={imageConfigurations}
            templates={imagePromptTemplates}
            usageLogs={imageUsageLogs}
            editingConfig={editingImageConfig}
            editingPrompt={editingImagePrompt}
            showCreatePrompt={showCreateImagePrompt}
            onEditConfig={setEditingImageConfig}
            onEditPrompt={setEditingImagePrompt}
            onShowCreatePrompt={setShowCreateImagePrompt}
          />
        )}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab: React.FC<{
  analytics: AIUsageAnalytics | null;
  configurations: AIConfiguration[];
  templates: PromptTemplate[];
  flags: FeatureFlag[];
}> = ({ analytics, configurations, templates, flags }) => (
  <div className="overview-tab">
    <div className="overview-stats">
      <div className="stat-card">
        <h3>Total Requests (30 days)</h3>
        <p className="stat-value">{analytics?.total_requests || 0}</p>
      </div>
      <div className="stat-card">
        <h3>Total Cost (30 days)</h3>
        <p className="stat-value">${analytics?.total_cost?.toFixed(2) || '0.00'}</p>
      </div>
      <div className="stat-card">
        <h3>Active Users</h3>
        <p className="stat-value">{analytics?.unique_users || 0}</p>
      </div>
      <div className="stat-card">
        <h3>Configurations</h3>
        <p className="stat-value">{configurations.length}</p>
      </div>
    </div>

    <div className="overview-grid">
      <div className="overview-section">
        <h3>AI Providers</h3>
        {configurations.map(config => (
          <div key={config.config_id} className="provider-item">
            <span className="provider-name">{config.provider_name}</span>
            <span className={`provider-status ${config.is_active ? 'active' : 'inactive'}`}>
              {config.is_active ? '🟢 Active' : '🔴 Inactive'}
            </span>
          </div>
        ))}
      </div>

      <div className="overview-section">
        <h3>Prompt Templates</h3>
        <p>{templates.length} templates configured</p>
        <p>{templates.filter(t => t.is_active).length} active</p>
      </div>

      <div className="overview-section">
        <h3>Feature Flags</h3>
        <p>{flags.length} flags configured</p>
        <p>{flags.filter(f => f.is_enabled).length} enabled</p>
      </div>
    </div>
  </div>
);

// Configuration Tab Component
const ConfigurationTab: React.FC<{
  providers: AIProvider[];
  configurations: AIConfiguration[];
  editingConfig: AIConfiguration | null;
  onEdit: (config: AIConfiguration) => void;
  onUpdate: (providerName: string, data: AIConfigurationUpdate) => void;
  onCancel: () => void;
}> = ({ providers, configurations, editingConfig, onEdit, onUpdate, onCancel }) => {
  const [formData, setFormData] = useState<AIConfigurationUpdate>({
    api_key: '',
    model_name: '',
    max_tokens: 1000, // Legacy field
    max_input_tokens: 12000,
    max_output_tokens: 5000,
    temperature: 0.7,
    top_p: 1.0,
    frequency_penalty: 0.0,
    presence_penalty: 0.0,
    cost_per_1k_input_tokens: '',
    cost_per_1k_output_tokens: '',
    daily_request_limit: 1000,
    monthly_cost_limit: '',
    is_active: false,
    is_production: false,
    api_endpoint: '',
    provider_is_active: false
  });

  useEffect(() => {
    if (editingConfig) {
      setFormData({
        api_key: '', // Don't pre-fill API key for security
        model_name: editingConfig.model_name,
        max_tokens: editingConfig.max_tokens,
        max_input_tokens: editingConfig.max_input_tokens || 12000,
        max_output_tokens: editingConfig.max_output_tokens || 5000,
        temperature: editingConfig.temperature,
        top_p: editingConfig.top_p,
        frequency_penalty: editingConfig.frequency_penalty,
        presence_penalty: editingConfig.presence_penalty,
        cost_per_1k_input_tokens: editingConfig.cost_per_1k_input_tokens,
        cost_per_1k_output_tokens: editingConfig.cost_per_1k_output_tokens,
        daily_request_limit: editingConfig.daily_request_limit,
        monthly_cost_limit: editingConfig.monthly_cost_limit,
        is_active: editingConfig.is_active,
        is_production: editingConfig.is_production,
        api_endpoint: editingConfig.api_endpoint || '',
        provider_is_active: editingConfig.provider_is_active ?? true
      });
    } else {
      // Reset form when not editing
      setFormData({
        api_key: '',
        model_name: '',
        max_tokens: 1000, // Legacy field
        max_input_tokens: 12000,
        max_output_tokens: 5000,
        temperature: 0.7,
        top_p: 1.0,
        frequency_penalty: 0.0,
        presence_penalty: 0.0,
        cost_per_1k_input_tokens: '',
        cost_per_1k_output_tokens: '',
        daily_request_limit: 1000,
        monthly_cost_limit: '',
        is_active: false,
        is_production: false,
        api_endpoint: '',
        provider_is_active: false
      });
    }
  }, [editingConfig]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingConfig) {
      onUpdate(editingConfig.provider_name, formData);
    }
  };

  return (
    <div className="configuration-tab">
      <h2>AI Provider Configurations</h2>

      {!editingConfig ? (
        <div className="configurations-list">
          {configurations.map(config => (
            <div key={config.config_id} className="config-card">
              <div className="config-header">
                <h3>{config.provider_name}</h3>
                <div className="config-status">
                  <span className={config.provider_is_active ? 'status-active' : 'status-inactive'}>
                    {config.provider_is_active ? '🟢 Provider Active' : '🔴 Provider Inactive'}
                  </span>
                  <span className={config.is_active ? 'status-active' : 'status-inactive'}>
                    {config.is_active ? 'Config Active' : 'Config Inactive'}
                  </span>
                  <span className={config.is_production ? 'env-production' : 'env-development'}>
                    {config.is_production ? 'Production' : 'Development'}
                  </span>
                </div>
              </div>

              <div className="config-details">
                <p><strong>API Endpoint:</strong> {config.api_endpoint || 'Not set'}</p>
                <p><strong>Model:</strong> {config.model_name}</p>
                <p><strong>Max Input Tokens:</strong> {config.max_input_tokens || config.max_tokens}</p>
                <p><strong>Max Output Tokens:</strong> {config.max_output_tokens || config.max_tokens}</p>
                <p><strong>Temperature:</strong> {config.temperature}</p>
                <p><strong>Monthly Limit:</strong> ${config.monthly_cost_limit || 'Not set'}</p>
                <p><strong>Last Used:</strong> {config.last_used_at ? new Date(config.last_used_at).toLocaleDateString() : 'Never'}</p>
              </div>

              <button onClick={() => onEdit(config)} className="edit-btn">
                Edit Configuration
              </button>
            </div>
          ))}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="config-form">
          <h3>Edit {editingConfig.provider_name} Configuration</h3>

          <div className="form-group">
            <label>API Key (leave blank to keep current)</label>
            <input
              type="password"
              placeholder="Enter new API key"
              value={formData.api_key || ''}
              onChange={(e) => setFormData({...formData, api_key: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>API Endpoint</label>
            <input
              type="text"
              placeholder="https://api.example.com or http://localhost:11434"
              value={formData.api_endpoint || ''}
              onChange={(e) => setFormData({...formData, api_endpoint: e.target.value})}
            />
            <small className="field-hint">The base URL for the AI provider's API (e.g., http://192.168.1.35:11434 for local Ollama)</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Model Name</label>
              <select
                value={formData.model_name || ''}
                onChange={(e) => setFormData({...formData, model_name: e.target.value})}
                required
              >
                <option value="">Select a model</option>
                {editingConfig && (() => {
                  const provider = providers.find(p => p.name === editingConfig.provider_name);
                  return provider?.configuration?.supported_models?.map(model => (
                    <option key={model} value={model}>{model}</option>
                  )) || [];
                })()}
              </select>
            </div>

            <div className="form-group">
              <label>Max Input Tokens</label>
              <input
                type="number"
                value={formData.max_input_tokens || ''}
                onChange={(e) => setFormData({...formData, max_input_tokens: parseInt(e.target.value)})}
                min="1000"
                max="100000"
                required
                placeholder="12000"
              />
              <small className="field-hint">Maximum tokens allowed for chapter input (recommended: 12000)</small>
            </div>

            <div className="form-group">
              <label>Max Output Tokens</label>
              <input
                type="number"
                value={formData.max_output_tokens || ''}
                onChange={(e) => setFormData({...formData, max_output_tokens: parseInt(e.target.value)})}
                min="100"
                max="20000"
                required
                placeholder="5000"
              />
              <small className="field-hint">Maximum tokens for AI response (recommended: 5000)</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Temperature</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="2"
                value={formData.temperature || ''}
                onChange={(e) => setFormData({...formData, temperature: parseFloat(e.target.value)})}
                required
              />
            </div>

            <div className="form-group">
              <label>Top P</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={formData.top_p || ''}
                onChange={(e) => setFormData({...formData, top_p: parseFloat(e.target.value)})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Frequency Penalty</label>
              <input
                type="number"
                step="0.1"
                min="-2"
                max="2"
                value={formData.frequency_penalty || ''}
                onChange={(e) => setFormData({...formData, frequency_penalty: parseFloat(e.target.value)})}
              />
            </div>

            <div className="form-group">
              <label>Presence Penalty</label>
              <input
                type="number"
                step="0.1"
                min="-2"
                max="2"
                value={formData.presence_penalty || ''}
                onChange={(e) => setFormData({...formData, presence_penalty: parseFloat(e.target.value)})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Cost per 1K Input Tokens ($)</label>
              <input
                type="text"
                value={formData.cost_per_1k_input_tokens || ''}
                onChange={(e) => setFormData({...formData, cost_per_1k_input_tokens: e.target.value})}
                placeholder="0.0015"
              />
            </div>

            <div className="form-group">
              <label>Cost per 1K Output Tokens ($)</label>
              <input
                type="text"
                value={formData.cost_per_1k_output_tokens || ''}
                onChange={(e) => setFormData({...formData, cost_per_1k_output_tokens: e.target.value})}
                placeholder="0.002"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Daily Request Limit</label>
              <input
                type="number"
                value={formData.daily_request_limit || ''}
                onChange={(e) => setFormData({...formData, daily_request_limit: parseInt(e.target.value)})}
                min="1"
              />
            </div>

            <div className="form-group">
              <label>Monthly Cost Limit ($)</label>
              <input
                type="text"
                value={formData.monthly_cost_limit || ''}
                onChange={(e) => setFormData({...formData, monthly_cost_limit: e.target.value})}
                placeholder="100.00"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.provider_is_active || false}
                  onChange={(e) => setFormData({...formData, provider_is_active: e.target.checked})}
                />
                Provider Active
              </label>
              <small className="field-hint">Enable/disable the entire provider (ai_providers table)</small>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active || false}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                Configuration Active
              </label>
              <small className="field-hint">Enable/disable this configuration (ai_configuration table)</small>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_production || false}
                  onChange={(e) => setFormData({...formData, is_production: e.target.checked})}
                />
                Production Environment
              </label>
              <small className="field-hint">Set this as the active production configuration</small>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn">Update Configuration</button>
            <button type="button" onClick={onCancel} className="cancel-btn">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};

// Prompts Tab Component
const PromptsTab: React.FC<{
  templates: PromptTemplate[];
  editingPrompt: PromptTemplate | null;
  showCreatePrompt: boolean;
  onEdit: (prompt: PromptTemplate) => void;
  onCreate: () => void;
  onUpdate: (templateId: string, data: PromptTemplateUpdate) => void;
  onDelete: (templateId: string) => void;
  onCreateSubmit: (data: PromptTemplateCreate) => void;
  onCancel: () => void;
  configurations: AIConfiguration[];
}> = ({
  templates,
  editingPrompt,
  showCreatePrompt,
  onEdit,
  onCreate,
  onUpdate,
  onDelete,
  onCreateSubmit,
  onCancel,
  configurations
}) => {
  const [createFormData, setCreateFormData] = useState<PromptTemplateCreate>({
    name: '',
    display_name: '',
    description: '',
    prompt_template: '',
    provider_id: '',
    category: 'analysis',
    is_active: true,
    is_default: false
  });

  const [updateFormData, setUpdateFormData] = useState<PromptTemplateUpdate>({
    display_name: '',
    description: '',
    prompt_template: '',
    category: 'analysis',
    is_active: true,
    is_default: false
  });

  useEffect(() => {
    if (editingPrompt) {
      setUpdateFormData({
        display_name: editingPrompt.display_name,
        description: editingPrompt.description,
        prompt_template: editingPrompt.prompt_template,
        category: editingPrompt.category,
        is_active: editingPrompt.is_active,
        is_default: editingPrompt.is_default
      });
    } else if (showCreatePrompt) {
      setCreateFormData({
        name: '',
        display_name: '',
        description: '',
        prompt_template: '',
        provider_id: configurations[0]?.provider_id || '',
        category: 'analysis',
        is_active: true,
        is_default: false
      });
    }
  }, [editingPrompt, showCreatePrompt, configurations]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPrompt) {
      onUpdate(editingPrompt.template_id, updateFormData);
    } else if (showCreatePrompt) {
      onCreateSubmit(createFormData);
    }
  };

  const handleDelete = (templateId: string) => {
    if (window.confirm('Are you sure you want to delete this prompt template?')) {
      onDelete(templateId);
    }
  };

  return (
    <div className="prompts-tab">
      <div className="prompts-header">
        <h2>Prompt Templates</h2>
        {!editingPrompt && !showCreatePrompt && (
          <button onClick={onCreate} className="submit-btn">Create New Template</button>
        )}
      </div>

      {!editingPrompt && !showCreatePrompt ? (
        <div className="templates-list">
          {templates.map(template => (
            <div key={template.template_id} className="config-card">
              <div className="config-header">
                <h3>{template.display_name}</h3>
                <div className="config-status">
                  <span className={template.is_active ? 'status-active' : 'status-inactive'}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {template.is_default && (
                    <span className="env-production">Default</span>
                  )}
                </div>
              </div>

              <div className="config-details">
                <p><strong>Name:</strong> {template.name}</p>
                <p><strong>Provider:</strong> {template.provider_name}</p>
                <p><strong>Category:</strong> {template.category}</p>
                <p><strong>Version:</strong> {template.version}</p>
                {template.description && (
                  <p><strong>Description:</strong> {template.description}</p>
                )}
              </div>

              <div className="template-preview">
                <h4>Template Preview:</h4>
                <pre className="prompt-preview">
                  {template.prompt_template.substring(0, 200)}
                  {template.prompt_template.length > 200 && '...'}
                </pre>
              </div>

              <div className="form-actions">
                <button onClick={() => onEdit(template)} className="edit-btn">
                  Edit Template
                </button>
                <button
                  onClick={() => handleDelete(template.template_id)}
                  className="cancel-btn"
                  style={{ marginLeft: '10px' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="config-form">
          <h3>{editingPrompt ? 'Edit' : 'Create'} Prompt Template</h3>

          {showCreatePrompt && (
            <>
              <div className="form-group">
                <label>Template Name (Internal)</label>
                <input
                  type="text"
                  value={createFormData.name || ''}
                  onChange={(e) => setCreateFormData({...createFormData, name: e.target.value})}
                  placeholder="chapter_analysis_default"
                  required
                />
              </div>

              <div className="form-group">
                <label>AI Provider</label>
                <select
                  value={createFormData.provider_id || ''}
                  onChange={(e) => setCreateFormData({...createFormData, provider_id: e.target.value})}
                  required
                >
                  <option value="">Select Provider</option>
                  {configurations.map(config => (
                    <option key={config.provider_id} value={config.provider_id}>
                      {config.provider_name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Display Name</label>
            <input
              type="text"
              value={editingPrompt ? updateFormData.display_name || '' : createFormData.display_name || ''}
              onChange={(e) => {
                if (editingPrompt) {
                  setUpdateFormData({...updateFormData, display_name: e.target.value});
                } else {
                  setCreateFormData({...createFormData, display_name: e.target.value});
                }
              }}
              placeholder="Chapter Analysis Template"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={editingPrompt ? updateFormData.description || '' : createFormData.description || ''}
              onChange={(e) => {
                if (editingPrompt) {
                  setUpdateFormData({...updateFormData, description: e.target.value});
                } else {
                  setCreateFormData({...createFormData, description: e.target.value});
                }
              }}
              placeholder="Template for analyzing chapter content and providing feedback"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              value={editingPrompt ? updateFormData.category || 'analysis' : createFormData.category || 'analysis'}
              onChange={(e) => {
                if (editingPrompt) {
                  setUpdateFormData({...updateFormData, category: e.target.value});
                } else {
                  setCreateFormData({...createFormData, category: e.target.value});
                }
              }}
              required
            >
              <option value="analysis">Analysis</option>
              <option value="feedback">Feedback</option>
              <option value="improvement">Improvement</option>
              <option value="creative">Creative</option>
            </select>
          </div>

          <div className="form-group">
            <label>Prompt Template</label>
            <textarea
              value={editingPrompt ? updateFormData.prompt_template || '' : createFormData.prompt_template || ''}
              onChange={(e) => {
                if (editingPrompt) {
                  setUpdateFormData({...updateFormData, prompt_template: e.target.value});
                } else {
                  setCreateFormData({...createFormData, prompt_template: e.target.value});
                }
              }}
              placeholder="You are an expert memoir writing coach. Analyze the following chapter content and provide detailed feedback on: 1) Writing style and flow 2) Emotional resonance 3) Clarity and coherence 4) Suggestions for improvement. Chapter content: {content}"
              rows={10}
              required
            />
            <small>Use {'{content}'} as a placeholder for the chapter content</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editingPrompt ? updateFormData.is_active || false : createFormData.is_active || false}
                  onChange={(e) => {
                    if (editingPrompt) {
                      setUpdateFormData({...updateFormData, is_active: e.target.checked});
                    } else {
                      setCreateFormData({...createFormData, is_active: e.target.checked});
                    }
                  }}
                />
                Active
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editingPrompt ? updateFormData.is_default || false : createFormData.is_default || false}
                  onChange={(e) => {
                    if (editingPrompt) {
                      setUpdateFormData({...updateFormData, is_default: e.target.checked});
                    } else {
                      setCreateFormData({...createFormData, is_default: e.target.checked});
                    }
                  }}
                />
                Set as Default Template
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn">
              {editingPrompt ? 'Update Template' : 'Create Template'}
            </button>
            <button type="button" onClick={onCancel} className="cancel-btn">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab: React.FC<{
  analytics: AIUsageAnalytics | null;
}> = ({ analytics }) => (
  <div className="analytics-tab">
    <h2>AI Usage Analytics</h2>

    {analytics ? (
      <>
        <div className="overview-stats">
          <div className="stat-card">
            <h3>Total Requests ({analytics.period_days} days)</h3>
            <p className="stat-value">{analytics.total_requests}</p>
          </div>
          <div className="stat-card">
            <h3>Total Cost</h3>
            <p className="stat-value">${analytics.total_cost.toFixed(2)}</p>
          </div>
          <div className="stat-card">
            <h3>Unique Users</h3>
            <p className="stat-value">{analytics.unique_users}</p>
          </div>
          <div className="stat-card">
            <h3>Avg Cost per Request</h3>
            <p className="stat-value">
              ${analytics.total_requests > 0 ? (analytics.total_cost / analytics.total_requests).toFixed(4) : '0.00'}
            </p>
          </div>
        </div>

        <div className="analytics-chart">
          <h3>Daily Usage Breakdown</h3>
          <div className="chart-container">
            {analytics.daily_breakdown.map((day, index) => (
              <div key={index} className="chart-bar">
                <div className="bar-label">{new Date(day.date).toLocaleDateString()}</div>
                <div className="bar-container">
                  <div
                    className="bar-fill"
                    style={{
                      height: `${Math.min((day.requests / Math.max(...analytics.daily_breakdown.map(d => d.requests))) * 100, 100)}%`
                    }}
                  />
                </div>
                <div className="bar-value">{day.requests}</div>
                <div className="bar-cost">${day.cost.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      </>
    ) : (
      <p>Loading analytics data...</p>
    )}
  </div>
);

// Feature Flags Tab Component
const FeatureFlagsTab: React.FC<{
  flags: FeatureFlag[];
  onUpdate: (flagName: string, data: FeatureFlagUpdate) => void;
}> = ({ flags, onUpdate }) => {
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [formData, setFormData] = useState<FeatureFlagUpdate>({});

  useEffect(() => {
    if (editingFlag) {
      setFormData({
        description: editingFlag.description,
        is_enabled: editingFlag.is_enabled,
        allowed_tiers: editingFlag.allowed_tiers,
        tier_limits: editingFlag.tier_limits
      });
    }
  }, [editingFlag]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingFlag) {
      onUpdate(editingFlag.name, formData);
      setEditingFlag(null);
    }
  };

  return (
    <div className="feature-flags-tab">
      <h2>AI Feature Flags</h2>

      {!editingFlag ? (
        <div className="flags-list">
          {flags.map(flag => (
            <div key={flag.flag_id} className="config-card">
              <div className="config-header">
                <h3>{flag.name}</h3>
                <div className="config-status">
                  <span className={flag.is_enabled ? 'status-active' : 'status-inactive'}>
                    {flag.is_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="config-details">
                {flag.description && (
                  <p><strong>Description:</strong> {flag.description}</p>
                )}
                {flag.allowed_tiers && (
                  <p><strong>Allowed Tiers:</strong> {flag.allowed_tiers.join(', ')}</p>
                )}
                {flag.tier_limits && (
                  <div>
                    <strong>Tier Limits:</strong>
                    <ul>
                      {Object.entries(flag.tier_limits).map(([tier, limit]) => (
                        <li key={tier}>{tier}: {limit}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <button onClick={() => setEditingFlag(flag)} className="edit-btn">
                Edit Flag
              </button>
            </div>
          ))}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="config-form">
          <h3>Edit Feature Flag: {editingFlag.name}</h3>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_enabled || false}
                onChange={(e) => setFormData({...formData, is_enabled: e.target.checked})}
              />
              Enabled
            </label>
          </div>

          <div className="form-group">
            <label>Allowed Tiers (comma-separated)</label>
            <input
              type="text"
              value={formData.allowed_tiers?.join(', ') || ''}
              onChange={(e) => setFormData({
                ...formData,
                allowed_tiers: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
              })}
              placeholder="free, pro, premium"
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn">Update Flag</button>
            <button type="button" onClick={() => setEditingFlag(null)} className="cancel-btn">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};

// Usage Logs Tab Component
const UsageLogsTab: React.FC<{
  logs: PaginatedAIUsageLogsResponse | null;
}> = ({ logs }) => (
  <div className="usage-logs-tab">
    <h2>AI Usage Logs</h2>

    {logs ? (
      <>
        <div className="logs-summary">
          <p>Showing {logs.logs.length} of {logs.total} logs (Page {logs.page} of {logs.pages})</p>
        </div>

        <div className="logs-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Model</th>
                <th>Tokens</th>
                <th>Cost</th>
                <th>Response Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.logs.map(log => (
                <tr key={log.usage_id}>
                  <td>{new Date(log.created_at).toLocaleString()}</td>
                  <td>{log.user_id.substring(0, 8)}...</td>
                  <td>{log.model_used}</td>
                  <td>{log.total_tokens}</td>
                  <td>${log.estimated_cost}</td>
                  <td>{log.response_time_ms}ms</td>
                  <td>
                    <span className={`status-${log.status.toLowerCase()}`}>
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ) : (
      <p>Loading usage logs...</p>
    )}
  </div>
);

// Image Generation Tab Component
const ImageGenerationTab: React.FC<{
  providers: AIImageProvider[];
  configurations: AIImageConfiguration[];
  templates: ImagePromptTemplate[];
  usageLogs: PaginatedAIImageUsageLogsResponse | null;
  editingConfig: AIImageConfiguration | null;
  editingPrompt: ImagePromptTemplate | null;
  showCreatePrompt: boolean;
  onEditConfig: (config: AIImageConfiguration) => void;
  onEditPrompt: (prompt: ImagePromptTemplate) => void;
  onShowCreatePrompt: (show: boolean) => void;
}> = ({
  providers,
  configurations,
  templates,
  usageLogs,
  editingConfig,
  editingPrompt,
  showCreatePrompt,
  onEditConfig,
  onEditPrompt,
  onShowCreatePrompt
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'configuration' | 'prompts' | 'usage'>('configuration');

  return (
    <div className="image-generation-tab">
      <h2>Image Generation Configuration</h2>

      <div className="image-sub-tabs">
        <button
          className={activeSubTab === 'configuration' ? 'active' : ''}
          onClick={() => setActiveSubTab('configuration')}
        >
          Configuration
        </button>
        <button
          className={activeSubTab === 'prompts' ? 'active' : ''}
          onClick={() => setActiveSubTab('prompts')}
        >
          Prompt Templates
        </button>
        <button
          className={activeSubTab === 'usage' ? 'active' : ''}
          onClick={() => setActiveSubTab('usage')}
        >
          Usage Logs
        </button>
      </div>

      <div className="image-sub-content">
        {activeSubTab === 'configuration' && (
          <ImageConfigurationSection
            providers={providers}
            configurations={configurations}
            editingConfig={editingConfig}
            onEdit={onEditConfig}
          />
        )}

        {activeSubTab === 'prompts' && (
          <ImagePromptsSection
            templates={templates}
            providers={providers}
            editingPrompt={editingPrompt}
            showCreatePrompt={showCreatePrompt}
            onEdit={onEditPrompt}
            onShowCreate={onShowCreatePrompt}
          />
        )}

        {activeSubTab === 'usage' && (
          <ImageUsageSection logs={usageLogs} />
        )}
      </div>
    </div>
  );
};

// Image Configuration Section
const ImageConfigurationSection: React.FC<{
  providers: AIImageProvider[];
  configurations: AIImageConfiguration[];
  editingConfig: AIImageConfiguration | null;
  onEdit: (config: AIImageConfiguration) => void;
}> = ({ providers, configurations, editingConfig, onEdit }) => {
  const [formData, setFormData] = useState<AIImageConfigurationUpdate>({});

  useEffect(() => {
    if (editingConfig) {
      setFormData({
        model_name: editingConfig.model_name,
        aspect_ratio: editingConfig.aspect_ratio,
        quality_level: editingConfig.quality_level,
        safety_filter_level: editingConfig.safety_filter_level,
        temperature: editingConfig.temperature,
        guidance_scale: editingConfig.guidance_scale,
        num_inference_steps: editingConfig.num_inference_steps,
        max_images_per_request: editingConfig.max_images_per_request,
        max_requests_per_minute: editingConfig.max_requests_per_minute,
        max_requests_per_day: editingConfig.max_requests_per_day,
        cost_per_image: editingConfig.cost_per_image,
        cost_per_hd_image: editingConfig.cost_per_hd_image,
        credits_per_image: editingConfig.credits_per_image,
        is_active: editingConfig.is_active,
        is_production: editingConfig.is_production
      });
    }
  }, [editingConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingConfig) return;

    try {
      const updated = await apiClient.updateImageConfiguration(editingConfig.provider_name, formData);
      alert('Image configuration updated successfully!');
      onEdit(null as any);
      window.location.reload(); // Reload to fetch updated data
    } catch (error) {
      console.error('Error updating image configuration:', error);
      alert('Failed to update image configuration');
    }
  };

  return (
    <div className="image-configuration-section">
      {!editingConfig ? (
        <div className="configurations-list">
          <h3>Image Generation Providers</h3>
          {configurations.map(config => (
            <div key={config.config_id} className="config-card">
              <div className="config-header">
                <h3>{config.provider_name}</h3>
                <div className="config-status">
                  <span className={config.is_active ? 'status-active' : 'status-inactive'}>
                    {config.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className={config.is_production ? 'env-production' : 'env-development'}>
                    {config.is_production ? 'Production' : 'Development'}
                  </span>
                </div>
              </div>

              <div className="config-details">
                <p><strong>Model:</strong> {config.model_name}</p>
                <p><strong>Aspect Ratio:</strong> {config.aspect_ratio || 'N/A'}</p>
                <p><strong>Quality:</strong> {config.quality_level || 'N/A'}</p>
                <p><strong>Safety Filter:</strong> {config.safety_filter_level || 'N/A'}</p>
                <p><strong>Cost per Image:</strong> ${config.cost_per_image.toFixed(4)}</p>
                <p><strong>Credits per Image:</strong> {config.credits_per_image} credits</p>
                <p><strong>Daily Limit:</strong> {config.max_requests_per_day} requests</p>
                <p><strong>Last Used:</strong> {config.last_used_at ? new Date(config.last_used_at).toLocaleDateString() : 'Never'}</p>
              </div>

              <button onClick={() => onEdit(config)} className="edit-btn">
                Edit Configuration
              </button>
            </div>
          ))}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="config-form">
          <h3>Edit {editingConfig.provider_name} Image Configuration</h3>

          <div className="form-group">
            <label>API Key (leave blank to keep current)</label>
            <input
              type="password"
              placeholder="Enter new API key"
              value={formData.api_key || ''}
              onChange={(e) => setFormData({...formData, api_key: e.target.value})}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Model Name</label>
              <input
                type="text"
                value={formData.model_name || ''}
                onChange={(e) => setFormData({...formData, model_name: e.target.value})}
                placeholder="gemini-2.5-flash-image"
                required
              />
            </div>

            <div className="form-group">
              <label>Aspect Ratio</label>
              <select
                value={formData.aspect_ratio || ''}
                onChange={(e) => setFormData({...formData, aspect_ratio: e.target.value})}
              >
                <option value="1:1">1:1 (Square)</option>
                <option value="16:9">16:9 (Landscape)</option>
                <option value="9:16">9:16 (Portrait)</option>
                <option value="4:3">4:3</option>
                <option value="3:4">3:4</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Quality Level</label>
              <select
                value={formData.quality_level || ''}
                onChange={(e) => setFormData({...formData, quality_level: e.target.value})}
              >
                <option value="standard">Standard</option>
                <option value="hd">HD</option>
              </select>
            </div>

            <div className="form-group">
              <label>Safety Filter Level</label>
              <select
                value={formData.safety_filter_level || ''}
                onChange={(e) => setFormData({...formData, safety_filter_level: e.target.value})}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Temperature</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="2"
                value={formData.temperature || ''}
                onChange={(e) => setFormData({...formData, temperature: parseFloat(e.target.value)})}
              />
            </div>

            <div className="form-group">
              <label>Guidance Scale</label>
              <input
                type="number"
                step="0.5"
                min="1"
                max="20"
                value={formData.guidance_scale || ''}
                onChange={(e) => setFormData({...formData, guidance_scale: parseFloat(e.target.value)})}
              />
            </div>

            <div className="form-group">
              <label>Inference Steps</label>
              <input
                type="number"
                min="10"
                max="150"
                value={formData.num_inference_steps || ''}
                onChange={(e) => setFormData({...formData, num_inference_steps: parseInt(e.target.value)})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Cost per Image ($)</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={formData.cost_per_image || ''}
                onChange={(e) => setFormData({...formData, cost_per_image: parseFloat(e.target.value)})}
                placeholder="0.02"
              />
            </div>

            <div className="form-group">
              <label>Cost per HD Image ($)</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={formData.cost_per_hd_image || ''}
                onChange={(e) => setFormData({...formData, cost_per_hd_image: parseFloat(e.target.value)})}
                placeholder="0.04"
              />
            </div>

            <div className="form-group">
              <label>Credits per Image</label>
              <input
                type="number"
                min="1"
                value={formData.credits_per_image || ''}
                onChange={(e) => setFormData({...formData, credits_per_image: parseInt(e.target.value)})}
                placeholder="50"
                required
              />
              <small className="field-hint">User-facing credits deducted per image generated</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Max Requests per Minute</label>
              <input
                type="number"
                min="1"
                value={formData.max_requests_per_minute || ''}
                onChange={(e) => setFormData({...formData, max_requests_per_minute: parseInt(e.target.value)})}
              />
            </div>

            <div className="form-group">
              <label>Max Requests per Day</label>
              <input
                type="number"
                min="1"
                value={formData.max_requests_per_day || ''}
                onChange={(e) => setFormData({...formData, max_requests_per_day: parseInt(e.target.value)})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active || false}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                Active
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_production || false}
                  onChange={(e) => setFormData({...formData, is_production: e.target.checked})}
                />
                Production Environment
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn">Update Configuration</button>
            <button type="button" onClick={() => onEdit(null as any)} className="cancel-btn">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};

// Image Prompts Section
const ImagePromptsSection: React.FC<{
  templates: ImagePromptTemplate[];
  providers: AIImageProvider[];
  editingPrompt: ImagePromptTemplate | null;
  showCreatePrompt: boolean;
  onEdit: (prompt: ImagePromptTemplate) => void;
  onShowCreate: (show: boolean) => void;
}> = ({ templates, providers, editingPrompt, showCreatePrompt, onEdit, onShowCreate }) => {
  const [createFormData, setCreateFormData] = useState<ImagePromptTemplateCreate>({
    name: '',
    display_name: '',
    description: '',
    analysis_prompt_template: '',
    image_prompt_prefix: '',
    image_prompt_suffix: '',
    negative_prompt: '',
    style_guidelines: '',
    aspect_ratio: '1:1',
    category: 'image_generation',
    is_active: true,
    is_default: false
  });

  const [updateFormData, setUpdateFormData] = useState<ImagePromptTemplateUpdate>({});

  useEffect(() => {
    if (editingPrompt) {
      setUpdateFormData({
        display_name: editingPrompt.display_name,
        description: editingPrompt.description,
        analysis_prompt_template: editingPrompt.analysis_prompt_template,
        image_prompt_prefix: editingPrompt.image_prompt_prefix,
        image_prompt_suffix: editingPrompt.image_prompt_suffix,
        negative_prompt: editingPrompt.negative_prompt,
        style_guidelines: editingPrompt.style_guidelines,
        aspect_ratio: editingPrompt.aspect_ratio,
        is_active: editingPrompt.is_active,
        is_default: editingPrompt.is_default
      });
    }
  }, [editingPrompt]);

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newTemplate = await apiClient.createImagePromptTemplate(createFormData);
      alert('Image prompt template created successfully!');
      onShowCreate(false);
      // Reload page to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Failed to create template');
    }
  };

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPrompt) return;

    try {
      await apiClient.updateImagePromptTemplate(editingPrompt.template_id, updateFormData);
      alert('Image prompt template updated successfully!');
      onEdit(null as any);
      window.location.reload();
    } catch (error) {
      console.error('Error updating template:', error);
      alert('Failed to update template');
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await apiClient.deleteImagePromptTemplate(templateId);
      alert('Template deleted successfully!');
      window.location.reload();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  return (
    <div className="image-prompts-section">
      <div className="prompts-header">
        <h3>Image Prompt Templates</h3>
        {!editingPrompt && !showCreatePrompt && (
          <button onClick={() => onShowCreate(true)} className="submit-btn">
            Create New Template
          </button>
        )}
      </div>

      {!editingPrompt && !showCreatePrompt ? (
        <div className="templates-list">
          {templates.map(template => (
            <div key={template.template_id} className="config-card">
              <div className="config-header">
                <h3>{template.display_name}</h3>
                <div className="config-status">
                  <span className={template.is_active ? 'status-active' : 'status-inactive'}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {template.is_default && (
                    <span className="env-production">Default</span>
                  )}
                </div>
              </div>

              <div className="config-details">
                <p><strong>Name:</strong> {template.name}</p>
                <p><strong>Provider:</strong> {template.provider_name || 'N/A'}</p>
                <p><strong>Usage Count:</strong> {template.usage_count}</p>
                {template.description && (
                  <p><strong>Description:</strong> {template.description}</p>
                )}
                {template.style_guidelines && (
                  <p><strong>Style Guidelines:</strong> {template.style_guidelines}</p>
                )}
                {template.aspect_ratio && (
                  <p><strong>Aspect Ratio:</strong> {template.aspect_ratio}</p>
                )}
                {template.image_prompt_prefix && (
                  <p><strong>Prompt Prefix:</strong> {template.image_prompt_prefix}</p>
                )}
                {template.image_prompt_suffix && (
                  <p><strong>Prompt Suffix:</strong> {template.image_prompt_suffix}</p>
                )}
                {template.negative_prompt && (
                  <p><strong>Negative Prompt:</strong> {template.negative_prompt}</p>
                )}
              </div>

              <div className="template-preview">
                <h4>Analysis Prompt Preview:</h4>
                <pre className="prompt-preview">
                  {template.analysis_prompt_template.substring(0, 200)}
                  {template.analysis_prompt_template.length > 200 && '...'}
                </pre>
              </div>

              <div className="form-actions">
                <button onClick={() => onEdit(template)} className="edit-btn">
                  Edit Template
                </button>
                <button
                  onClick={() => handleDelete(template.template_id)}
                  className="cancel-btn"
                  style={{ marginLeft: '10px' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : showCreatePrompt ? (
        <form onSubmit={handleCreateSubmit} className="config-form">
          <h3>Create Image Prompt Template</h3>

          <div className="form-group">
            <label>Template Name (Internal)</label>
            <input
              type="text"
              value={createFormData.name}
              onChange={(e) => setCreateFormData({...createFormData, name: e.target.value})}
              placeholder="chapter_image_custom"
              required
            />
          </div>

          <div className="form-group">
            <label>Display Name</label>
            <input
              type="text"
              value={createFormData.display_name}
              onChange={(e) => setCreateFormData({...createFormData, display_name: e.target.value})}
              placeholder="Custom Chapter Image Template"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={createFormData.description}
              onChange={(e) => setCreateFormData({...createFormData, description: e.target.value})}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Analysis Prompt Template</label>
            <textarea
              value={createFormData.analysis_prompt_template}
              onChange={(e) => setCreateFormData({...createFormData, analysis_prompt_template: e.target.value})}
              placeholder="You are an expert at creating image generation prompts for memoir content. Analyze the chapter and create a detailed image prompt..."
              rows={8}
              required
            />
            <small>Use {'{chapter_title}'} and {'{chapter_content}'} as placeholders</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Image Prompt Prefix</label>
              <input
                type="text"
                value={createFormData.image_prompt_prefix || ''}
                onChange={(e) => setCreateFormData({...createFormData, image_prompt_prefix: e.target.value})}
                placeholder="Create a photorealistic image:"
              />
            </div>

            <div className="form-group">
              <label>Image Prompt Suffix</label>
              <input
                type="text"
                value={createFormData.image_prompt_suffix || ''}
                onChange={(e) => setCreateFormData({...createFormData, image_prompt_suffix: e.target.value})}
                placeholder=", high detail, professional photography"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Negative Prompt</label>
            <textarea
              value={createFormData.negative_prompt || ''}
              onChange={(e) => setCreateFormData({...createFormData, negative_prompt: e.target.value})}
              placeholder="blurry, low quality, distorted, cartoon"
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>Style Guidelines</label>
            <textarea
              value={createFormData.style_guidelines || ''}
              onChange={(e) => setCreateFormData({...createFormData, style_guidelines: e.target.value})}
              placeholder="Photorealistic memoir illustration with natural lighting"
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>Aspect Ratio</label>
            <select
              value={createFormData.aspect_ratio || '1:1'}
              onChange={(e) => setCreateFormData({...createFormData, aspect_ratio: e.target.value})}
            >
              <option value="1:1">1:1 (Square)</option>
              <option value="9:16">9:16 (Portrait - for covers)</option>
              <option value="16:9">16:9 (Landscape)</option>
              <option value="4:3">4:3 (Standard)</option>
              <option value="3:4">3:4 (Portrait)</option>
            </select>
            <small>Image aspect ratio for this template</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={createFormData.is_active}
                  onChange={(e) => setCreateFormData({...createFormData, is_active: e.target.checked})}
                />
                Active
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={createFormData.is_default}
                  onChange={(e) => setCreateFormData({...createFormData, is_default: e.target.checked})}
                />
                Set as Default
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn">Create Template</button>
            <button type="button" onClick={() => onShowCreate(false)} className="cancel-btn">Cancel</button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleUpdateSubmit} className="config-form">
          <h3>Edit Image Prompt Template</h3>

          <div className="form-group">
            <label>Display Name</label>
            <input
              type="text"
              value={updateFormData.display_name || ''}
              onChange={(e) => setUpdateFormData({...updateFormData, display_name: e.target.value})}
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={updateFormData.description || ''}
              onChange={(e) => setUpdateFormData({...updateFormData, description: e.target.value})}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Analysis Prompt Template</label>
            <textarea
              value={updateFormData.analysis_prompt_template || ''}
              onChange={(e) => setUpdateFormData({...updateFormData, analysis_prompt_template: e.target.value})}
              rows={8}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Image Prompt Prefix</label>
              <input
                type="text"
                value={updateFormData.image_prompt_prefix || ''}
                onChange={(e) => setUpdateFormData({...updateFormData, image_prompt_prefix: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Image Prompt Suffix</label>
              <input
                type="text"
                value={updateFormData.image_prompt_suffix || ''}
                onChange={(e) => setUpdateFormData({...updateFormData, image_prompt_suffix: e.target.value})}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Negative Prompt</label>
            <textarea
              value={updateFormData.negative_prompt || ''}
              onChange={(e) => setUpdateFormData({...updateFormData, negative_prompt: e.target.value})}
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>Style Guidelines</label>
            <textarea
              value={updateFormData.style_guidelines || ''}
              onChange={(e) => setUpdateFormData({...updateFormData, style_guidelines: e.target.value})}
              placeholder="Photorealistic memoir illustration with natural lighting"
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>Aspect Ratio</label>
            <select
              value={updateFormData.aspect_ratio || '1:1'}
              onChange={(e) => setUpdateFormData({...updateFormData, aspect_ratio: e.target.value})}
            >
              <option value="1:1">1:1 (Square)</option>
              <option value="9:16">9:16 (Portrait - for covers)</option>
              <option value="16:9">16:9 (Landscape)</option>
              <option value="4:3">4:3 (Standard)</option>
              <option value="3:4">3:4 (Portrait)</option>
            </select>
            <small>Image aspect ratio for this template</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={updateFormData.is_active || false}
                  onChange={(e) => setUpdateFormData({...updateFormData, is_active: e.target.checked})}
                />
                Active
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={updateFormData.is_default || false}
                  onChange={(e) => setUpdateFormData({...updateFormData, is_default: e.target.checked})}
                />
                Set as Default
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn">Update Template</button>
            <button type="button" onClick={() => onEdit(null as any)} className="cancel-btn">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};

// Image Usage Section
const ImageUsageSection: React.FC<{
  logs: PaginatedAIImageUsageLogsResponse | null;
}> = ({ logs }) => (
  <div className="image-usage-section">
    <h3>Image Generation Usage Logs</h3>

    {logs ? (
      <>
        <div className="logs-summary">
          <p>Showing {logs.logs.length} of {logs.total} logs (Page {logs.page} of {logs.pages})</p>
        </div>

        <div className="logs-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Model</th>
                <th>Prompt (truncated)</th>
                <th>Cost</th>
                <th>Response Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.logs.map(log => (
                <tr key={log.usage_id}>
                  <td>{new Date(log.created_at).toLocaleString()}</td>
                  <td>{log.user_id.substring(0, 8)}...</td>
                  <td>{log.model_used}</td>
                  <td className="prompt-cell">{log.prompt_used}</td>
                  <td>${log.estimated_cost}</td>
                  <td>{log.response_time_ms || 'N/A'}ms</td>
                  <td>
                    <span className={`status-${log.status.toLowerCase()}`}>
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ) : (
      <p>No usage logs available. Logs will appear here once image generation is used.</p>
    )}
  </div>
);

export default AdminAIDashboard;
